import { supabase } from '@/integrations/supabase/client';
import type {
  AzureAddress,
  GeocodeResult,
  AddressValidation,
  SearchAddressOptions,
  BatchGeocodeRequest,
  BatchGeocodeResponse,
  BatchGeocodeMatch,
} from '@/lib/types/azure';
import { formatStreetLine } from '@/lib/addressFormat';
import { captureException } from '@/lib/sentry';

/** The `address` object on a Search v1 result, as far as we read it. */
interface RawAzureAddress {
  streetNumber?: string;
  streetName?: string;
  municipality?: string;
  municipalitySubdivision?: string;
  countrySubdivision?: string;
  countrySubdivisionName?: string;
  countrySecondarySubdivision?: string;
  postalCode?: string;
  extendedPostalCode?: string;
  country?: string;
  countryCode?: string;
  freeformAddress?: string;
}

/**
 * Azure Maps Service that uses Supabase Edge Function as proxy
 * This way users don't need Azure AD accounts - authentication is handled server-side
 */
class AzureMapsProxyService {
  /**
   * Call Azure Maps via Supabase Edge Function
   */
  private async callAzureMaps(endpoint: string, params: Record<string, any> = {}, method = 'GET', body?: any) {
    const { data, error } = await supabase.functions.invoke('azure-maps-proxy', {
      body: {
        endpoint,
        method,
        params,
        body
      }
    });

    if (error) {
      throw new Error(`Azure Maps request failed: ${error.message}`);
    }

    return data;
  }

  /**
   * Validate and correct an address
   */
  public async validateAddress(input: string, options?: SearchAddressOptions): Promise<AddressValidation> {
    try {
      const results = await this.searchAddress(input, { ...options, limit: 1 });

      if (!results || results.length === 0) {
        return {
          isValid: false,
          confidence: 0,
          originalInput: input,
        };
      }

      const topResult = results[0];
      const corrections: AddressValidation['corrections'] = [];

    // Compare input with result to find corrections
      const inputLower = input.toLowerCase();
      const resultAddress = topResult.address;

      // Check for corrections in various fields
      if (resultAddress.formattedAddress && !inputLower.includes(resultAddress.formattedAddress.toLowerCase())) {
        corrections.push({
          field: 'formattedAddress',
          original: input,
          corrected: resultAddress.formattedAddress,
        });
      }

      return {
        isValid: topResult.score > 0.7,
        confidence: topResult.score,
        originalInput: input,
        suggestedAddress: resultAddress,
        corrections: corrections.length > 0 ? corrections : undefined,
        matchCodes: topResult.matchCodes,
      };
    } catch (error) {
      console.error('Address validation failed:', error);
      return {
        isValid: false,
        confidence: 0,
        originalInput: input,
      };
    }
  }

  /**
   * Geocode an address (convert address to coordinates)
   */
  public async geocodeAddress(address: string, options?: SearchAddressOptions): Promise<GeocodeResult[]> {
    const results = await this.searchAddress(address, options);
    return results;
  }

  /**
   * Reverse geocode (convert coordinates to address)
   */
  public async reverseGeocode(lat: number, lon: number, options?: { language?: string }): Promise<AzureAddress | null> {
    try {
      const params: Record<string, string> = {
        query: `${lat},${lon}`,
      };

      if (options?.language) {
        params.language = options.language;
      }

      const result = await this.callAzureMaps('/search/address/reverse/json', params);

      if (!result?.addresses?.length) {
        return null;
      }

      return this.mapAddress(result.addresses[0].address);
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return null;
    }
  }

  /**
   * Query parameters shared by the Search Address and Search Fuzzy calls.
   */
  private buildSearchParams(query: string, options?: SearchAddressOptions): Record<string, string> {
    const params: Record<string, string> = {
      query,
      limit: (options?.limit || 10).toString(),
    };

    if (options?.countrySet?.length) {
      params.countrySet = options.countrySet.join(',');
    }
    if (options?.language) {
      params.language = options.language;
    }
    if (options?.lat !== undefined) {
      params.lat = options.lat.toString();
    }
    if (options?.lon !== undefined) {
      params.lon = options.lon.toString();
    }
    if (options?.radius !== undefined) {
      params.radius = options.radius.toString();
    }
    if (options?.extendedPostalCodesFor) {
      params.extendedPostalCodesFor = options.extendedPostalCodesFor.join(',');
    }
    if (options?.view) {
      params.view = options.view;
    }
    return params;
  }

  /**
   * Geocode a complete address — GET /search/address/json.
   *
   * This is the FINAL-answer call: `score` is Azure's bounded 0–1 match
   * confidence (`matchConfidence.score`) and is what `validateAddress`
   * thresholds and `gis_locations.confidence_score` stores. For as-you-type
   * suggestions use `suggestAddresses()` instead.
   *
   * `countrySet` is a hard filter, and it is what makes Dutch input work:
   * "1015 CJ 123" is Tijuana without it and Keizersgracht 123 with it. But a
   * hard filter also turns "10 Downing Street London" into junk NL streets,
   * so with `widenBelowScore` set the restricted search is retried worldwide
   * when its best match is weak, and the better answer wins.
   */
  public async searchAddress(query: string, options?: SearchAddressOptions): Promise<GeocodeResult[]> {
    try {
      const params = this.buildSearchParams(query, options);
      const result = await this.callAzureMaps('/search/address/json', params);
      const results: GeocodeResult[] = (result?.results ?? []).map((item: any) => this.mapToGeocodeResult(item));

      const threshold = options?.widenBelowScore;
      const restricted = Boolean(options?.countrySet?.length);
      if (threshold !== undefined && restricted && (results[0]?.score ?? 0) < threshold) {
        const { countrySet: _dropped, widenBelowScore: _once, ...worldwide } = options ?? {};
        const wide = await this.searchAddress(query, worldwide);
        if ((wide[0]?.score ?? 0) > (results[0]?.score ?? 0)) {
          return wide;
        }
      }

      return results;
    } catch (error) {
      console.error('Address search failed:', error);
      return [];
    }
  }

  /**
   * As-you-type address suggestions — GET /search/fuzzy/json with
   * `typeahead=true`, the shape Microsoft's Search best-practices page
   * prescribes for autocomplete. `idxSet=PAD,Addr,Str` keeps it to point
   * addresses, address ranges and streets (no POIs, no cities).
   *
   * Pass `lat`/`lon` and NOT `countrySet`: a soft bias is enough to rank
   * "Kerkstraat 12" in the right country while still finding a Brussels or
   * London address an NL org types in. Measured: bias alone resolves every
   * NL postcode+number form; the hard filter returns nothing for foreign
   * input.
   *
   * SCORE CAVEAT: the mapper reads `matchConfidence.score` first, but fuzzy
   * results were measured WITHOUT that field, so what comes through is the
   * top-level `score`: a bounded 0–1 blend only when a bias point is
   * passed, otherwise an unbounded relative rank (5.8, 8.2 …). It is
   * clamped here, and callers mark it `confidenceSource: 'rank'` so it is
   * never stored as a confidence — `saveLocation` re-scores through
   * `searchAddress()` first.
   */
  public async suggestAddresses(
    query: string,
    options?: SearchAddressOptions & {
      /**
       * Also return points of interest (businesses, venues), ranked by Azure
       * in the same list as the addresses — fuzzy is its "single-line
       * search" for exactly that. A POI item carries `poi` and `type: 'POI'`.
       */
      includePoi?: boolean;
    },
  ): Promise<GeocodeResult[]> {
    try {
      const { includePoi, ...searchOptions } = options ?? {};
      const params = {
        ...this.buildSearchParams(query, searchOptions),
        typeahead: 'true',
        idxSet: includePoi ? 'PAD,Addr,Str,POI' : 'PAD,Addr,Str',
      };
      const result = await this.callAzureMaps('/search/fuzzy/json', params);
      if (!result?.results) {
        return [];
      }
      return result.results.map((item: { type?: string }) => {
        const mapped = item.type === 'POI' ? this.mapToPOIResult(item) : this.mapToGeocodeResult(item);
        const score = Number.isFinite(mapped.score) ? Math.min(1, Math.max(0, mapped.score)) : 0;
        return { ...mapped, score };
      });
    } catch (error) {
      // No query text in the extra: it is a fragment of someone's address.
      captureException(error, {
        context: 'azureMapsProxy.suggestAddresses',
        queryLength: query.length,
        countrySet: options?.countrySet,
        biased: options?.lat !== undefined,
      });
      return [];
    }
  }

  /**
   * Synchronous batch geocode — POST /search/address/batch/sync/json.
   *
   * Max 100 items per call, 60s server-side timeout. Results come back in
   * REQUEST ORDER with a per-item statusCode, which is the whole contract:
   * callers index-align by position. An unmatched address comes back as
   * `statusCode: 200` with an EMPTY results array (not an error status), and
   * either way yields `null` in that slot rather than throwing — one unmatched
   * address must never cost the batch.
   *
   * Returns the raw `type` alongside the mapped result because SCORE IS NOT A
   * QUALITY SIGNAL here: Azure answers a city-only query with `type:
   * "Geography"` and a score of 1.0. Callers that need "is this really a
   * building address?" must read `resultType`.
   *
   * Prefer this over `batchGeocode()` below, which targets the ASYNC endpoint:
   * that one answers `202` + a `Location` header the proxy edge function cannot
   * parse, so it has never worked. See the note there.
   */
  public async batchGeocodeSync(
    queries: { query: string; countrySet?: string[] }[],
  ): Promise<(BatchGeocodeMatch | null)[]> {
    if (queries.length > 100) {
      throw new Error('Synchronous batch geocoding is limited to 100 addresses at a time');
    }
    if (queries.length === 0) return [];

    // A batch item is a partial URL, not a bare address: everything after the
    // path, properly URL-encoded (Azure passes it verbatim to Search Address).
    const batchItems = queries.map(({ query, countrySet }) => {
      const params = new URLSearchParams({ query, limit: '1' });
      if (countrySet?.length) params.set('countrySet', countrySet.join(','));
      return { query: `?${params.toString()}` };
    });

    const result = await this.callAzureMaps(
      '/search/address/batch/sync/json',
      {},
      'POST',
      { batchItems },
    );

    const items: unknown[] = Array.isArray(result?.batchItems) ? result.batchItems : [];
    return queries.map((_, i) => {
      const item = items[i] as
        | { statusCode?: number; response?: { results?: unknown[] } }
        | undefined;
      if (item?.statusCode !== 200) return null;
      const first = item.response?.results?.[0] as
        | { type?: string; score?: number; matchConfidence?: { score?: number } }
        | undefined;
      if (!first) return null;

      // matchConfidence.score is the modern 0–1 field; the top-level `score` is
      // documented as deprecated and historically carried an UNBOUNDED relative
      // score, so clamp — confidence_score is numeric(3,2) and would overflow.
      const raw = first.matchConfidence?.score ?? first.score ?? 0;
      return {
        result: this.mapToGeocodeResult(first),
        resultType: first.type ?? null,
        confidence: Math.min(1, Math.max(0, Number.isFinite(raw) ? raw : 0)),
      };
    });
  }

  /**
   * Batch geocode multiple addresses (up to 100 at once).
   *
   * DEAD AND BROKEN — kept only because `useAddressLookup.batchValidate` still
   * references it. `/search/address/batch/json` is the ASYNC endpoint: it
   * answers `202 Accepted` with a `Location` header to poll, and the
   * azure-maps-proxy edge function unconditionally calls `.json()` on the
   * response, so this cannot succeed. It also sends bare address strings where
   * Azure expects the `?query=…` partial-URL form. Use `batchGeocodeSync()`.
   */
  public async batchGeocode(queries: string[]): Promise<BatchGeocodeResponse> {
    if (queries.length > 100) {
      throw new Error('Batch geocoding is limited to 100 addresses at a time');
    }

    try {
      const batchItems: BatchGeocodeRequest['batchItems'] = queries.map(query => ({ query }));

      const result = await this.callAzureMaps('/search/address/batch/json', {}, 'POST', { batchItems });

      return result;
    } catch (error) {
      console.error('Batch geocoding failed:', error);
      throw error;
    }
  }

  /**
   * Get structured address search (more structured than free-form search)
   */
  public async structuredSearch(address: {
    streetNumber?: string;
    streetName?: string;
    municipality?: string;
    countrySubdivision?: string;
    postalCode?: string;
    countryCode?: string;
  }): Promise<GeocodeResult[]> {
    try {
      const params: Record<string, string> = {};

      Object.entries(address).forEach(([key, value]) => {
        if (value) {
          params[key] = value;
        }
      });

      const result = await this.callAzureMaps('/search/address/structured/json', params);

      if (!result?.results) {
        return [];
      }

      return result.results.map((item: any) => this.mapToGeocodeResult(item));
    } catch (error) {
      console.error('Structured address search failed:', error);
      return [];
    }
  }

  /**
   * Map an Azure `address` object to our AzureAddress. The street line is
   * built in the country's own order (NL "Kerkstraat 12B", GB "12B Church
   * Street") and the raw number/name ride alongside so nobody has to split
   * the line back apart.
   */
  private mapAddress(address: RawAzureAddress): AzureAddress {
    const countryCode: string | undefined = address.countryCode || undefined;
    const streetNumber: string | undefined = address.streetNumber || undefined;
    const streetName: string | undefined = address.streetName || undefined;
    return {
      addressLine: streetName ? formatStreetLine(streetNumber, streetName, countryCode) : undefined,
      streetNumber,
      streetName,
      locality: address.municipality || address.municipalitySubdivision,
      adminDistrict: address.countrySubdivision || address.countrySubdivisionName,
      adminDistrict2: address.countrySecondarySubdivision,
      postalCode: address.postalCode || address.extendedPostalCode,
      countryRegion: address.country,
      countryRegionISO2: countryCode,
      formattedAddress: address.freeformAddress,
    };
  }

  /**
   * Map Azure Maps API response to our GeocodeResult type
   */
  private mapToGeocodeResult(item: any): GeocodeResult {
    return {
      id: item.id,
      type: 'Point',
      // matchConfidence.score is the bounded 0–1 field; the top-level score
      // is documented as deprecated (see batchGeocodeSync) — same order there.
      score: item.matchConfidence?.score ?? item.score ?? 0,
      matchCodes: item.matchConfidence?.matchCodes,
      address: this.mapAddress(item.address || {}),
      position: {
        lat: item.position?.lat || 0,
        lon: item.position?.lon || 0,
      },
      viewport: item.viewport,
      addressRanges: item.addressRanges,
    };
  }

  /**
   * Map Azure Maps POI API response to our GeocodeResult type
   */
  private mapToPOIResult(item: any): GeocodeResult {
    const poi = item.poi || {};

    return {
      id: item.id,
      type: 'POI',
      score: item.matchConfidence?.score ?? item.score ?? 0,
      matchCodes: item.matchConfidence?.matchCodes,
      address: this.mapAddress(item.address || {}),
      position: {
        lat: item.position?.lat || 0,
        lon: item.position?.lon || 0,
      },
      viewport: item.viewport,
      poi: {
        name: poi.name,
        phone: poi.phone,
        categories: poi.categories || [],
        classifications: poi.classifications || [],
      },
    };
  }

  /**
   * Calculate distance between two points (in meters)
   */
  public calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }
}

// Export singleton instance
export const azureMapsProxy = new AzureMapsProxyService();
